import { CardContent, Switch } from "@libs/zapi-ui/shadcn";
import { CheckIcon, XIcon } from "@libs/zapi-ui/assets";
import { useCallback, useState } from "react";
import { AppButton, ProseWrapper } from "@libs/zapi-ui/custom";
import { useParams } from "react-router-dom";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { HighlightedDiff2 } from "./_HighlightedDiff2";
import { useDiffStore } from "@/zapi-frontend/stores/diff";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { markdownToHtmlSync } from "@/zapi-frontend/utils/markdown";
import { hasPendingDiffs } from "@/zapi-frontend/utils/diff-utils";

export function DiffViewTab({ className }: { className?: string }) {
  const { id } = useParams();
  const [showDiffs, setShowDiffs] = useState(true);
  const projectId = typeof id === "string" ? id : "";
  const { activeChapter } = useChapterStore();
  const chapterId = activeChapter?.id?.toString() || "";
  const { setContent } = useEditorStore();
  const { getDiffEditor, acceptChanges, rejectChanges, getAcceptedVersion } = useDiffStore();
  const htmlContent = getDiffEditor(projectId, chapterId)?.htmlContent;
  const hasDiffs = !!htmlContent && hasPendingDiffs(htmlContent);
  const acceptedVersion = getAcceptedVersion(projectId, chapterId);
  const diffDoneCallback = useCallback(
    (content: string) => {
      setContent?.(content);
    },
    [setContent]
  );

  if (!acceptedVersion || !hasDiffs) return null;

  return (
    <CardContent className={"p-4 overflow-y-auto h-full " + className}>
      <div className="space-y-4 flex flex-col h-full">
        {showDiffs && <HighlightedDiff2 diffDoneCallback={diffDoneCallback} />}
        {!showDiffs && (
          <ProseWrapper className="max-h-[calc(100vh-124px-56px-40px-48px)]">
            <div dangerouslySetInnerHTML={{ __html: markdownToHtmlSync(acceptedVersion) }}></div>
          </ProseWrapper>
        )}
        <div className="flex space-x-2 rounded-lg shadow-xs border p-2 items-center">
          <AppButton
            data-testid="diff-view-accept-all-button"
            appearance="text"
            onClick={() => {
              const chapterId = activeChapter?.id?.toString();
              if (!chapterId) return;
              acceptChanges(projectId, chapterId, diffDoneCallback);
            }}
          >
            <CheckIcon />
            Accept all
          </AppButton>
          <AppButton
            data-testid="diff-view-reject-all-button"
            appearance="text"
            onClick={() => {
              const chapterId = activeChapter?.id?.toString();
              if (!chapterId) return;
              rejectChanges(projectId, chapterId, diffDoneCallback);
            }}
          >
            <XIcon /> Reject all
          </AppButton>
          <div className="flex ml-auto gap-2 items-center">
            <div className="flex rounded-sm overflow-hidden">
              <span className="bg-green-100 h-[20px] w-[20px]" />
              <span className="bg-red-100  h-[20px] w-[20px]" />
            </div>
            Show diffs
            <Switch
              data-testid="diff-view-show-diffs-switch"
              className="h-5 w-8"
              checked={showDiffs}
              onClick={() => {
                setShowDiffs((prev) => !prev);
              }}
            />
          </div>
        </div>
      </div>
    </CardContent>
  );
}
